import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { Badge, Form, InputGroup, Spinner } from 'react-bootstrap';
import { FiDatabase, FiSearch, FiUsers } from 'react-icons/fi';
import { UserModel } from '../../_core/_model.ts';
import { GetUsers } from '../../_core/_request.ts';
import { useToast } from '../../context/ToastContext.tsx';
import DropdownWithSearch from '../../UIComponents/DropdownWithSearch.tsx';
import { OptionType } from '../../UIComponents/MultiSelectDropdownProps';
import { ComponentLayout } from '../Layouts/ComponentLayout.tsx';
import { UserDataPreviewResponse } from './_core/_model.ts';
import { GetUserDataPreview } from './_core/_request.ts';


export default function UserDataPreview() {
    const [users, setUsers] = useState<UserModel[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [previewData, setPreviewData] = useState<UserDataPreviewResponse | null>(null);

    // States for UI
    const [loadingUsers, setLoadingUsers] = useState<boolean>(true);
    const [loadingData, setLoadingData] = useState<boolean>(false);

    // Store search queries dynamically: { [key: string]: string }
    const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
    const { showToast } = useToast();

    // 1. Fetch Users on mount
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoadingUsers(true);
                const response = await GetUsers({ userID: 0, userName: "", email: "", roleID: null, roleGroupID: null, managerUserID: null, appliationAccess: null });
                setUsers(response.data || []);
            } catch (error) {
                console.error("Failed to fetch users", error);
                showToast?.({ message: "Failed to load users.", type: "error" });
            } finally {
                setLoadingUsers(false);
            }
        };
        fetchUsers();
    }, []);

    // 2. Fetch Data when user is selected
    useEffect(() => {
        if (!selectedUserId) {
            setPreviewData(null);
            setSearchQueries({});
            return;
        }

        const fetchData = async () => {
            try {
                setLoadingData(true);
                const response = await GetUserDataPreview(selectedUserId);
                setPreviewData(response.data);

                // Initialize empty search queries for each key returned in the dictionary
                const initialSearchState: Record<string, string> = {};
                Object.keys(response.data || {}).forEach(key => {
                    initialSearchState[key] = '';
                });
                setSearchQueries(initialSearchState);

            } catch (error) {
                let pVal: any = {
                    "BusinessUnits": [
                        "Rain Bird Corp-Sales Cntral Sv",
                        "Golf Online Store credit cust",
                        "Rain Bird Corp Consumer Prod's",
                        "Rain Bird Mexico S.deR.L.deC.V",
                        "Rain Bird Europe",
                        "Rain Bird RBOTO",
                        "Rain Bird Australia",
                        "Rain Bird RBRAC",
                        "Rain Bird Brazil",
                        "Rain Bird China",
                        "Rain Bird International",
                        "Rain Bird Corp OnlineSales Div",
                        "Rain Bird RBJOR"
                    ],
                    "CorpCompany": [
                        "GROWERS SUPPLY & IRRIG. INC",
                        "SUISUN VALLEY FRUIT GRWRS",
                        "B&T WHOLESALE DISTRIBUTORS",
                        "CASA MCGREGOR S.A",
                        "ANDERSON NUNES FERREIRA",
                        "HUGO XAVIER MORENO GARZON",
                        "EMERY-WATERHOUSE CO.",
                        "BARACUI COMERCIAL LTDA",
                        "AG MIDDLE EAST W.L.L",
                        "GREGORY GRIGORIAN",
                        "AUTOMATIC SUPPLY",
                        "CRISTIAN ALEXIS ALTAMIRANO JAUREGUI",
                        "KENTUCKY TURF NURSERY PTY LTD",
                        "ALAN DE OLIVEIRA RIBEIRO",
                        "SEGO INDUSTRIES",
                        "Coley Electric & Plumbing Supply, Inc."
                    ],
                    "ProductBrand": [
                        "COM Controllers",
                        "CTR Plastic Res Valves",
                        "Company 60",
                        "CP Riser Mounted Impacts",
                        "CTR Spray Nozzles",
                        "Ag Micro Sprays & Spinners",
                        "Ag Miscellaneous",
                        "CP Canister Impacts",
                        "GLOBAL AG SERVICES",
                        "COM Central Controls",
                        "CTR Impact/Shrubs",
                        "INT Buy Outs"
                    ],
                    "Region": [
                        "INTL - Australia / New Zealand",
                        "Turf - San Diego",
                        "CP - Midwest",
                        "GLF - Rain Bird Employee",
                        "INTL - ISRAEL",
                        "INTL - Canada",
                        "CP - East",
                        "INTL - Africa",
                        "INTL - Europe",
                        "INTL - Rain Bird Employee",
                        "AG- 1018 Northeast/Great Lakes",
                        "GLF-PUMP NATIONAL WARRANTY",
                        "GLF - West",
                        "AG - 1009 S. CAL/C. CAL/NV",
                        "INTL - Rac",
                        "GLF - Non Golf Distributor",
                        "Turf - Pacific Northwest",
                        "AG - 1011 Mountain States",
                        "CP - West",
                        "Turf - Florida",
                        "INTL - INDIAN SUB CONTINENT",
                        "CP - Rain Bird Employee"
                    ],
                    "SBU": [
                        "Commercial",
                        "Golf",
                        "Contractor",
                        "Consumer Products",
                        "Ag",
                        "Landscape Drip",
                        "Accessories",
                        "OtO"
                    ]
                };
                setPreviewData(pVal);

                // Initialize empty search queries for each key returned in the dictionary
                const initialSearchState: Record<string, string> = {};
                Object.keys(pVal || {}).forEach(key => {
                    initialSearchState[key] = '';
                });
                setSearchQueries(initialSearchState);

                console.error("Failed to fetch user data preview", error);
                showToast?.({ message: "Failed to load preview data.", type: "error" });
            } finally {
                setLoadingData(false);
            }
        };
        fetchData();
    }, [selectedUserId]);

    // Map users to Dropdown options
    const userOptions: OptionType[] = useMemo(() => {
        return users.map(u => ({
            key: u.userID,
            value: `${u.userName} (${u.email})`
        }));
    }, [users]);

    // Handle individual search input changes
    const handleSearchChange = (key: string, value: string) => {
        setSearchQueries(prev => ({
            ...prev,
            [key]: value
        }));
    };

    return (
        <ComponentLayout
            title={
                <div className="d-flex align-items-center gap-2">
                    <FiUsers size={22} className="text-primary" />
                    <span>User Data Preview</span>
                </div>
            }
            subHeader={
                <div className="text-muted small px-3 pb-3">
                    Select a user to preview their assigned business units, companies, and roles.
                </div>
            }
            exButtons={
                <div style={{ minWidth: '350px' }}>
                    <DropdownWithSearch
                        label={loadingUsers ? "Loading users..." : "Select a User..."}
                        options={userOptions}
                        value={selectedUserId}
                        onChange={(val) => setSelectedUserId(val as number)}
                        icon={<FiUsers />}
                        disabled={loadingUsers}
                    />
                </div>
            }
            bodyHeight={0}
            objectType={undefined}
            cBody={
                <div className="d-flex flex-column h-100 p-3 overflow-auto custom-scrollbar">
                    {/* Empty State */}
                    {!selectedUserId && !loadingData && (
                        <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 text-muted py-5">
                            <FiDatabase size={48} className="mb-3 opacity-50" />
                            <h5 className="text-theme">No User Selected</h5>
                            <p>Please select a user from the dropdown above to view their data preview.</p>
                        </div>
                    )}

                    {/* Loading State */}
                    {loadingData && (
                        <div className="d-flex justify-content-center align-items-center py-5 flex-grow-1">
                            <Spinner animation="border" variant="primary" />
                            <span className="ms-3 text-muted">Loading preview data...</span>
                        </div>
                    )}

                    {/* Data Visualization Grid */}
                    <AnimatePresence mode="wait">
                        {previewData && !loadingData && (
                            <motion.div
                                className="row g-4 m-0 pb-4"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                {Object.entries(previewData).map(([key, items], index) => {
                                    const searchQuery = (searchQueries[key] || '').toLowerCase();
                                    const filteredItems = items.filter(item =>
                                        item.toLowerCase().includes(searchQuery)
                                    );

                                    return (
                                        <motion.div
                                            className="col-12 col-lg-6 col-xl-4 d-flex"
                                            key={key}
                                            initial={{ opacity: 0, scale: 0.95 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ delay: index * 0.05 }}
                                        >
                                            <div className="card bg-theme-card border-secondary-subtle shadow-sm w-100 d-flex flex-column rounded-3 overflow-hidden">

                                                {/* Card Header */}
                                                <div className="card-header bg-transparent border-bottom border-secondary-subtle p-3 d-flex justify-content-between align-items-center">
                                                    <h6 className="m-0 fw-bold text-theme">
                                                        {key}
                                                    </h6>
                                                    <Badge bg="primary" className="rounded-pill px-3 fw-normal">
                                                        {items.length} {items.length === 1 ? 'Item' : 'Items'}
                                                    </Badge>
                                                </div>

                                                {/* Independent Search Bar */}
                                                <div className="p-3 border-bottom border-secondary-subtle bg-theme-card-subtle">
                                                    <InputGroup size="sm">
                                                        <InputGroup.Text className="bg-transparent border-secondary-subtle text-muted">
                                                            <FiSearch />
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder={`Search ${key}...`}
                                                            value={searchQueries[key] || ''}
                                                            onChange={(e) => handleSearchChange(key, e.target.value)}
                                                            className="bg-transparent border-secondary-subtle text-theme shadow-none"
                                                        />
                                                    </InputGroup>
                                                </div>

                                                {/* Scrollable List Area */}
                                                <div
                                                    className="card-body p-0 custom-scrollbar flex-grow-1"
                                                    style={{ maxHeight: '350px', overflowY: 'auto' }}
                                                >
                                                    {filteredItems.length > 0 ? (
                                                        <div className="list-group list-group-flush">
                                                            {filteredItems.map((item, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    className="list-group-item bg-transparent text-theme border-secondary-subtle py-2 px-3 d-flex align-items-center"
                                                                    style={{ fontSize: '0.9rem' }}
                                                                >
                                                                    <div
                                                                        className="rounded-circle bg-primary opacity-75 me-3"
                                                                        style={{ width: '6px', height: '6px', minWidth: '6px' }}
                                                                    />
                                                                    <span className="text-break">{item}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="p-4 text-center text-muted small">
                                                            No matches found for "{searchQueries[key]}"
                                                        </div>
                                                    )}
                                                </div>

                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            }
        />
    );
}